import React, { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

/**
 * Route-level Suspense fallback. Intentionally minimal.
 *
 * Why: every page in the app owns its own internal skeleton that mirrors its
 * actual content shape (calendar grid, plan cards, settings rows, etc.).
 * If this fallback also drew a fake "header + 3 cards + 3 lines" skeleton,
 * the user would see TWO different skeletons flash by — one generic, then
 * the real one. So fast loads keep the layout slot warm with an empty pane
 * (no spinner→skeleton flash), then let the page's own skeleton render.
 *
 * F-117 (UAT 2026-06-13): the timeout affordance used to fire at 10s with a
 * WARNING triangle + "Refresh to try again" — but the app's first-load /
 * cold-boot can take 20-50s on uncached chunks (F-082), so that alarm fired
 * DURING a normal slow-but-successful load and read as a failure. Now:
 *   <600ms  → empty pane (fast loads stay flash-free)
 *   600ms+  → a calm centered spinner so the user knows it's loading
 *   25s+    → a gentle "still loading, you can refresh" affordance (softer than
 *             a warning; genuine wedged chunks are auto-reloaded by the
 *             vite:preloadError handler in main.tsx).
 */
export const PageContentLoader = (): React.ReactElement => {
  const [showSpinner, setShowSpinner] = useState(false);
  const [showTimeout, setShowTimeout] = useState(false);

  useEffect(() => {
    const spinnerTimer = setTimeout(() => setShowSpinner(true), 600);
    const timeoutTimer = setTimeout(() => setShowTimeout(true), 25000);
    return () => {
      clearTimeout(spinnerTimer);
      clearTimeout(timeoutTimer);
    };
  }, []);

  if (showTimeout) {
    return (
      <div className="flex flex-1 items-center justify-center min-h-[400px] p-6">
        <div className="text-center space-y-4">
          <Spinner size="lg" className="mx-auto" />
          <div className="space-y-2">
            <h3 className="text-lg font-medium text-foreground">Still loading…</h3>
            <p className="text-sm text-muted-foreground max-w-md">
              This is taking longer than usual. You can keep waiting, or refresh.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (showSpinner) {
    return (
      <div className="flex flex-1 items-center justify-center min-h-[400px]">
        <Spinner />
      </div>
    );
  }

  return <div aria-hidden="true" className="flex-1" />;
};

export default PageContentLoader;
